import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Loader } from "@package/subframe/components/Loader";
import { FeatherArrowLeft, FeatherPlus } from "@subframe/core";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";

import { FeatureGate } from "@/components/shared/FeatureGate";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useGetEmergencyLightCheckQueryControllerGet } from "@/generated/emergency-light-checks/emergency-light-checks";
import type { EmergencyLightCheckItemResponseDto } from "@/generated/model";
import {
  EMERGENCY_LIGHT_CHECK_COPY,
  NO_ROOM_LABEL,
  toEmergencyLightCheckKind,
} from "@/lib/emergency-light-checks";
import { FEATURES } from "@/lib/features";
import { memberDisplayName } from "@/lib/member-display";
import { canRaiseFollowUp } from "@/lib/roles";

export const Route = createFileRoute(
  "/_authenticated/emergency-light-checks/$checkId",
)({
  component: EmergencyLightCheckDetailPageRoute,
});

function EmergencyLightCheckDetailPageRoute() {
  return (
    <FeatureGate feature={FEATURES.MONITORING} featureName="Monitoring">
      <EmergencyLightCheckDetailPage />
    </FeatureGate>
  );
}

function EmergencyLightCheckDetailPage() {
  const { checkId } = Route.useParams();
  const navigate = useNavigate();
  const { isPlatformAdmin, userRoleType } = useAuth();
  const { selectedNurseryId, nurseryRole } = useNursery();

  const canRaise = canRaiseFollowUp({
    isPlatformAdmin,
    userRoleType,
    nurseryRole,
  });

  // Always refetch on open: a follow-up on a failed unit is usually completed
  // elsewhere (Task Manager, another device), and the app's global cache
  // (5-minute staleTime, refetchOnMount false) would otherwise show its old
  // status until a reload.
  const { data, isLoading } = useGetEmergencyLightCheckQueryControllerGet(
    checkId,
    {
      query: { refetchOnMount: "always" },
    },
  );
  const check = data?.data;
  const kind = toEmergencyLightCheckKind(check?.kind);
  const copy = EMERGENCY_LIGHT_CHECK_COPY[kind];

  const goBack = () =>
    navigate({ to: "/emergency-light-checks", search: { kind } });

  const raiseFollowUp = (item: EmergencyLightCheckItemResponseDto) => {
    navigate({
      to: "/follow-up-actions/create",
      search: {
        nurseryId: check?.nurseryId ?? selectedNurseryId ?? "",
        // The create route treats the legacy questionnaire params as required,
        // so they are passed empty rather than omitted.
        scheduledQuestionnaireId: "",
        answerId: "",
        questionText: "",
        checklistName: "",
        emergencyLightCheckId: checkId,
        emergencyLightCheckItemId: item.id,
        emergencyLightName: item.lightName,
      },
    });
  };

  return (
    <div className="flex w-full max-w-2xl flex-col items-start gap-6 bg-white animate-fade-in">
      <div className="flex w-full items-start gap-2">
        <IconButton
          aria-label="Back"
          icon={<FeatherArrowLeft />}
          onClick={goBack}
        />
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
          <span className="w-full text-metrics font-metrics text-primary-navy-800">
            {copy.title}
          </span>
          {check && (
            <span className="text-body font-body text-subtext-color">
              {format(new Date(check.submittedAt), "d MMM yyyy, HH:mm")} ·
              Logged by{" "}
              {check.recordedBy
                ? memberDisplayName(check.recordedBy)
                : "Unknown"}
            </span>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex w-full items-center justify-center py-12">
          <Loader size="large" />
        </div>
      ) : !check ? (
        <div className="flex w-full flex-col items-start gap-2 rounded-md border border-neutral-200 p-6">
          <span className="text-body font-body text-default-font">
            This check could not be found.
          </span>
        </div>
      ) : (
        <div className="flex w-full flex-col items-start gap-3">
          {check.items.map((item) => (
            <div
              key={item.id}
              className={`flex w-full flex-col items-start gap-3 rounded-lg border p-4 ${
                item.passed ? "border-neutral-200" : "border-red-200 bg-red-50"
              }`}
            >
              <div className="flex w-full flex-wrap items-center justify-between gap-3">
                <div className="flex flex-col items-start gap-0.5">
                  <span className="text-body-bold font-body-bold text-default-font">
                    {item.lightName}
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    {item.roomName ?? NO_ROOM_LABEL}
                  </span>
                </div>
                {item.passed ? (
                  <Badge variant="success">Pass</Badge>
                ) : (
                  <Badge variant="error">Fail</Badge>
                )}
              </div>

              {item.comment && (
                <span className="text-caption font-caption text-subtext-color italic">
                  &ldquo;{item.comment}&rdquo;
                </span>
              )}

              {item.followUpActions.length > 0 && (
                <div className="flex w-full flex-col items-start gap-1">
                  <span className="text-caption-bold font-caption-bold text-subtext-color">
                    Follow-up tasks
                  </span>
                  {item.followUpActions.map((action) => (
                    <div
                      key={action.id}
                      className="flex w-full items-center gap-2"
                    >
                      <span className="grow text-caption font-caption text-default-font">
                        {action.description}
                      </span>
                      <Badge variant="neutral">{action.status}</Badge>
                    </div>
                  ))}
                </div>
              )}

              {!item.passed && canRaise && (
                <Button
                  variant="neutral-secondary"
                  size="small"
                  icon={<FeatherPlus />}
                  onClick={() => raiseFollowUp(item)}
                >
                  Raise follow-up
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
